import React, { useContext, useEffect, useState } from "react";
import { LoadingIcon, TopDropdown } from "../../.././../components";
import { getGreenhouses, getHarvestedPlots } from "../../../../utils/api";
import { UserContext } from "../../../../context/UserContext";
import { useLocalStorage } from "../../../../hooks";
import dayjs from "dayjs";

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  greenHouseLabel,
  setGreenHouseLabel,
  blockLabel,
  setBlockLabel,
  selectedGreenhouses,
  setSelectedGreenhouses,
}) => {
  const TWO_WEEKS_AGO = dayjs().subtract(14, "day");

  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const buildHarvestMap = (harvestResp) => {
    const map = new Map();
    harvestResp?.greenhouses?.forEach((gh) => {
      gh.plots?.forEach((p) => {
        if (p.harvested_date) {
          map.set(`${gh.id}-${p.id}`, p.harvested_date);
        }
      });
    });
    return map;
  };

  const isOldHarvestedBay = (gh, harvestMap) => {
    if (!gh?.plots?.length) return false;
    return gh.plots.every((plot) => {
      const iso = harvestMap.get(`${gh.id}-${plot.id}`);
      return iso && dayjs(iso).isBefore(TWO_WEEKS_AGO);
    });
  };

  const enrichWithOldHarvestFlag = async (
    orgId,
    siteId,
    cropId,
    greenhouseList,
  ) => {
    if (!greenhouseList.length) return greenhouseList;
    const greenhouseIds = new Set();
    const plotIds = new Set();

    for (const greenhouse of greenhouseList) {
      greenhouseIds.add(greenhouse.id);
      for (const row of greenhouse.plots) {
        plotIds.add(row.id);
      }
    }
    if (greenhouseIds.size === 0) return {};

    const resp = await getHarvestedPlots(
      orgId,
      siteId,
      cropId,
      Array.from(greenhouseIds),
      Array.from(plotIds),
    );
    const harvestMap = buildHarvestMap(resp.data.data);

    return greenhouseList.map((g) => ({
      ...g,
      _isOldHarvested: isOldHarvestedBay(g, harvestMap),
    }));
  };

  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    const res = await getGreenhouses(orgId, siteId, cropId);
    const greenhouseList = Array.isArray(res?.data?.data) ? res.data.data : [];
    let arr = greenhouseList.map((greenhouse) => ({
      name: greenhouse.name,
      id: greenhouse.id,
      plots: greenhouse.plots,
      metadata: greenhouse.metadata,
    }));

    arr = await enrichWithOldHarvestFlag(orgId, siteId, cropId, arr);
    setGreenhouseData(arr);

    setSelectedGreenhouses(
      arr.filter((g) => !g._isOldHarvested).map((g) => g.name),
    );
    setGreenhouseLoading(false);
  };

  // Fetch greenhouses on org/site/crop change
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      const siteMetaData = currentSite.metadata[currentCrop.name] || {};
      setSiteMetadata(siteMetaData);

      if (siteMetaData?.greenhouse_name) {
        setGreenHouseLabel(siteMetaData.greenhouse_name);
      }
      if (siteMetaData?.block_name) {
        setBlockLabel(siteMetaData.block_name);
      }

      fetchGreenhouses(currentOrg, currentSite?.id, currentCrop?.id);
    }
  }, [currentSite, currentOrg, currentCrop]);

  // Set loading state
  useEffect(() => {
    setDropdownLoading(
      orgLoading || siteLoading || cropLoading || greenhouseLoading,
    );
  }, [
    orgLoading,
    siteLoading,
    cropLoading,
    greenhouseLoading,
    setDropdownLoading,
  ]);

  useEffect(() => {
    if (
      greenhouseData?.length > 0 &&
      (!selectedGreenhouses || selectedGreenhouses.length === 0)
    ) {
      const allGHNames = greenhouseData.map((g) => g.name);
      setSelectedGreenhouses(allGHNames);
    }
  }, [greenhouseData]);

  const greenhouseDropdownRender = (menu) => {
    if (!greenhouseData || greenhouseData.length === 0) {
      return menu;
    }

    const allGHNames = greenhouseData.map((g) => g.name);
    const allSelected =
      selectedGreenhouses &&
      selectedGreenhouses.length === greenhouseData.length;

    const toggleAll = () => {
      if (allSelected) {
        // Deselect all
        setSelectedGreenhouses([]);
      } else {
        // Select all
        setSelectedGreenhouses(allGHNames);
      }
    };

    return (
      <>
        <div
          style={{
            padding: "4px 12px",
            borderBottom: "1px solid #f0f0f0",
            cursor: "pointer",
            background: "#fff",
            fontWeight: 500,
          }}
          onMouseDown={(e) => e.preventDefault()} // prevent the menu from closing
          onClick={toggleAll}
        >
          {allSelected ? "Deselect All" : "Select All"}
        </div>
        {menu}
      </>
    );
  };

  return (
    <>
      {orgLoading || siteLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4 !font-MuseoSans">
          {/* Org Dropdown (ID-based) */}
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          {/* Site Dropdown (ID-based) */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop Dropdown (ID-based) */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          <TopDropdown
            label={greenHouseLabel + "s:"}
            val={selectedGreenhouses}
            maxTagCount={2}
            placeholder="Select Bays"
            classString="flex-1 min-w-[250px] sm:mb-2"
            changeCallback={setSelectedGreenhouses}
            mode="multiple"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={greenhouseData?.map((gh) => ({
              value: gh.name,
              label: gh.name,
            }))}
            popupRender={greenhouseDropdownRender}
          />
        </div>
      )}
    </>
  );
};

export default Dropdowns;
